export interface GuidePanelHistory {
  entries: string[];
  index: number;
}

type StoredGuidePanelHistory = Record<string, GuidePanelHistory>;

export const MAX_GUIDE_PANEL_HISTORY_ENTRIES = 10;

export const EMPTY_GUIDE_PANEL_HISTORY: GuidePanelHistory = {
  entries: [],
  index: -1,
};

export const guidePanelHistoryStorageKey = (baseKey: string): string =>
  `${baseKey}:guide-panel-history:v1`;

const normalizeGuidePanelHistory = (value: unknown): GuidePanelHistory => {
  if (!value || typeof value !== 'object') return EMPTY_GUIDE_PANEL_HISTORY;
  const candidate = value as Partial<GuidePanelHistory>;
  if (
    !Array.isArray(candidate.entries) ||
    typeof candidate.index !== 'number'
  ) {
    return EMPTY_GUIDE_PANEL_HISTORY;
  }
  const allEntries = candidate.entries.filter(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0
  );
  const removed = Math.max(
    0,
    allEntries.length - MAX_GUIDE_PANEL_HISTORY_ENTRIES
  );
  const entries = allEntries.slice(removed);
  const index = Math.max(
    -1,
    Math.min(entries.length - 1, Math.trunc(candidate.index) - removed)
  );
  return { entries, index };
};

export const pushGuidePanelHistory = (
  history: GuidePanelHistory,
  guideId: string
): GuidePanelHistory => {
  if (history.entries[history.index] === guideId) return history;
  const entries = [
    ...history.entries.slice(0, history.index + 1),
    guideId,
  ].slice(-MAX_GUIDE_PANEL_HISTORY_ENTRIES);
  return { entries, index: entries.length - 1 };
};

export const readGuidePanelHistory = (
  storageKey: string,
  userLogin: string | null
): GuidePanelHistory => {
  if (!userLogin) return EMPTY_GUIDE_PANEL_HISTORY;
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(storageKey) ?? '{}'
    ) as StoredGuidePanelHistory;
    return normalizeGuidePanelHistory(stored[userLogin]);
  } catch {
    return EMPTY_GUIDE_PANEL_HISTORY;
  }
};

export const writeGuidePanelHistory = (
  storageKey: string,
  userLogin: string | null,
  history: GuidePanelHistory
): void => {
  if (!userLogin) return;
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(storageKey) ?? '{}'
    ) as StoredGuidePanelHistory;
    stored[userLogin] = normalizeGuidePanelHistory(history);
    window.localStorage.setItem(storageKey, JSON.stringify(stored));
  } catch {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          [userLogin]: normalizeGuidePanelHistory(history),
        })
      );
    } catch {
      // Storage failure must not block the help panel.
    }
  }
};

export const previousGuideId = (history: GuidePanelHistory): string | null =>
  history.index > 0 ? history.entries[history.index - 1] : null;

export const nextGuideId = (history: GuidePanelHistory): string | null =>
  history.index >= 0 && history.index < history.entries.length - 1
    ? history.entries[history.index + 1]
    : null;

export const moveGuidePanelHistoryBack = (
  history: GuidePanelHistory
): GuidePanelHistory =>
  previousGuideId(history) ? { ...history, index: history.index - 1 } : history;

export const moveGuidePanelHistoryForward = (
  history: GuidePanelHistory
): GuidePanelHistory =>
  nextGuideId(history) ? { ...history, index: history.index + 1 } : history;
